'use client';

/**
 * Tapestry image export.
 *
 * Captures a live tapestry DOM subtree (the canvas root) to a PNG and triggers
 * a browser download. The tapestry is a stack of DOM + inline-SVG layers (not a
 * <canvas>), so we rasterize it with `html-to-image`, which clones the node,
 * inlines remote images and web fonts, then draws the result to a canvas.
 *
 * Backgrounds are captured from the host's static floor layer (a plain CSS
 * `background-image` div holding the last fully-revealed background), NOT the
 * fade-in reveal layers — those are tagged `data-export-ignore` so a mid-fade
 * partial-opacity layer never lands in the capture; export always reflects the
 * settled background. The floor PNG is served with `Access-Control-Allow-Origin:
 * *`, so html-to-image inlines it clean.
 *
 * `cacheBust` forces html-to-image's inlining fetch to use a fresh, query-keyed
 * URL so it can't pick up a no-CORS cached response (the floor layer also loads
 * its URL via plain CSS `background-image`, i.e. no-CORS) — which would
 * otherwise fail the embed and drop the background from the capture.
 *
 * `html-to-image` is imported lazily so it stays out of the initial bundle, and
 * a host that vendored this renderer without installing the dep only breaks the
 * export button — not its build.
 *
 * Nodes tagged `data-export-ignore` (and their subtrees) are dropped from the
 * capture — used to keep on-canvas chrome (the fullscreen toggle, the layout
 * snackbar) out of the exported image.
 *
 * Fonts: html-to-image embeds only the @font-face rules of families it finds
 * on the node's HTML descendants — it never looks inside SVG. The classic
 * canvas got away with that because its HTML title wears the same face as
 * its textpaths; the scene board's text is all SVG, in faces its HTML title
 * does not use, and exported in the browser's fallback (TT-1174 note 2). So
 * the families are gathered here over every descendant and handed over as
 * the font CSS to embed, built by html-to-image's own fetcher against a
 * stand-in of HTML spans wearing them.
 */

/** Every font family computed on an element under `node` that holds text. */
function familiesUsed(node) {
  const out = new Set();
  const walker = document.createTreeWalker(node, NodeFilter.SHOW_TEXT);
  const seen = new Set();
  for (let t = walker.nextNode(); t; t = walker.nextNode()) {
    const el = t.parentElement;
    if (!el || seen.has(el)) continue;
    seen.add(el);
    for (const f of getComputedStyle(el).fontFamily.split(',')) out.add(f.trim().replace(/["']/g, ''));
  }
  return out;
}

/** The @font-face CSS (fonts inlined as data URLs) for everything `node` wears. */
async function fontCssFor(node, getFontEmbedCSS) {
  const probe = document.createElement('div');
  for (const family of familiesUsed(node)) {
    const span = document.createElement('span');
    span.style.fontFamily = `"${family}"`;
    probe.appendChild(span);
  }
  // The probe is read for its computed styles only; it must be in the
  // document for those to resolve, and it never paints.
  probe.style.display = 'none';
  document.body.appendChild(probe);
  try {
    return await getFontEmbedCSS(probe);
  } finally {
    probe.remove();
  }
}

function downloadDataUrl(dataUrl, fileName) {
  const link = document.createElement('a');
  link.download = fileName;
  link.href = dataUrl;
  link.click();
}

// What the capture keeps: everything but tagged chrome and any video — a
// still has no frame to take from a clip, and html-to-image's attempt to
// draw one fails the whole export (a board whose art plays an intro clip
// exported nothing, TT-1174 note 2); the art's settled image beside it is
// what the still shows.
const captured = (node) => !(node instanceof Element && (node.hasAttribute('data-export-ignore') || node.tagName === 'VIDEO'));

/**
 * Capture `node` as a PNG and download it.
 * @param {HTMLElement} node       the element to snapshot (the canvas root)
 * @param {object}      [opts]
 * @param {string}      [opts.fileName='tapestry.png']
 * @param {number}      [opts.pixelRatio=2]  upscale factor for a crisp file
 */
export async function exportNodeToPng(node, { fileName = 'tapestry.png', pixelRatio = 2 } = {}) {
  if (!node) return;
  const { toPng, getFontEmbedCSS } = await import('html-to-image');
  const fontEmbedCSS = await fontCssFor(node, getFontEmbedCSS);
  const dataUrl = await toPng(node, { pixelRatio, filter: captured, cacheBust: true, fontEmbedCSS });
  downloadDataUrl(dataUrl, fileName);
}

/**
 * A canvas's export handle, whole: capture `node` named for the meeting;
 * a failure is logged under `tag` and never thrown at the host.
 */
export async function exportTapestryPng(node, { meetingName, dateStr, tag = 'TapestryCanvas' } = {}) {
  if (!node) return;
  try {
    await exportNodeToPng(node, { fileName: tapestryFileName(meetingName, dateStr) });
  } catch (err) {
    console.error(`[${tag}] image export failed:`, err);
  }
}

/** Build a filesystem-friendly export name from the meeting title + date. */
export function tapestryFileName(meetingName, dateStr) {
  const slug = (s) =>
    String(s || '')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  const parts = [slug(meetingName), slug(dateStr)].filter(Boolean);
  return `tapestry${parts.length ? `-${parts.join('-')}` : ''}.png`;
}
